/**
 * ENG-5688 (redesigned) — `agt impersonate {connect|current|exit}`.
 *
 * The CLI side of the operator-impersonates-agent v0 flow. The operator
 * gets a short redeem token (XXXX-XXXX) from the webapp (ENG-5702's
 * "Impersonate this agent" button) and pastes:
 *
 *     agt impersonate connect XXXX-XXXX
 *
 * The CLI:
 *  1. Validates the token shape; refuses obvious garbage before any
 *     server round-trip.
 *  2. POSTs the token to the API's PUBLIC /impersonate/agent/redeem
 *     endpoint (ENG-5701, amended by this PR to use short-token
 *     credentials) — no Authorization, no X-Team-Slug. The token IS
 *     the credential, so this works in a clean shell with no
 *     `AGT_API_KEY` set (ENG-5688 AC4).
 *  3. Receives the bundle:
 *       { token, expires_at, session_id, agent { id, code_name, team_id },
 *         artifacts: { "CLAUDE.md", ".mcp.json" } }
 *  4. Writes the persona files to ~/.augmented-impersonate/<code-name>/
 *  5. Backs up the operator's existing project files and symlinks the
 *     persona files in (preserves CR-feedback invariants on atomicity,
 *     strict root check, and refused symlinks — see ../lib/impersonate-fs-ops.ts).
 *  6. Stashes the manifest atomically with the host the token was
 *     exchanged against so `exit` calls /stop on the right environment.
 *  7. Prints agent details + restart-Claude-Code prompt.
 *
 * `agt impersonate current` reads the active manifest; `agt impersonate
 * exit` writes the stop audit row, restores backups, and clears the
 * manifest.
 *
 * Gated behind AGT_IMPERSONATE_ENABLED (see ../lib/impersonate-flag.ts)
 * until the surrounding pieces (ENG-5689 channel-MCP refusal, ENG-5702
 * webapp button) land and the end-to-end smoke test runs against prod.
 */

import chalk from 'chalk';
import ora from 'ora';
import { spawn } from 'node:child_process';
import { existsSync, readFileSync, writeFileSync } from 'node:fs';
import { delimiter, dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';

import { ApiError, api } from '../lib/api-client.js';
import { PROD_AGT_HOST } from '../lib/config.js';
import { error, info, success } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';
import { requireImpersonateEnabled } from '../lib/impersonate-flag.js';
import {
  clearActiveManifest,
  ensureCodeNameDir,
  ensureImpersonateWorkdir,
  isExpired,
  listActiveManifests,
  readActiveManifest,
  writeActiveManifest,
  type BackupKind,
  type HookBackup,
  type ImpersonateManifest,
  type StatusLineBackup,
} from '../lib/impersonate-state.js';
import {
  findGitWorkTreeRoot,
  restoreSwapped,
  swapInPersonaFile,
} from '../lib/impersonate-fs-ops.js';
import {
  registerIntroduceHook,
  unregisterIntroduceHook,
} from '../lib/impersonate-hook.js';
import {
  registerStatusLine,
  unregisterStatusLine,
} from '../lib/impersonate-statusline.js';
import {
  buildIntroduction,
  parseIdentityFromClaudeMd,
  parseIntegrationsFromMcpJson,
  type ParsedIdentity,
} from '../lib/impersonate-introduce.js';
import { rewriteMcpJsonForImpersonation } from '../lib/impersonate-mcp-rewrite.js';

const SWAP_FILES = ['CLAUDE.md', '.mcp.json'] as const;
const AGENT_IMPERSONATION_HEADER = 'X-Agent-Impersonation';

// ENG-5688 (+ amends ENG-5701): short-token redeem format. Mirrors
// /host/setup's provisioning_tokens — two 4-char Crockford-style
// alphanumeric segments joined by a hyphen, e.g. "A3K7-NP5V".
const REDEEM_TOKEN_RE = /^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/i;

/** Server response from POST /impersonate/agent/redeem. */
interface RedeemBundle {
  token: string;
  /**
   * ENG-5874: the portable agent-session token the augmented MCP server uses
   * directly as Bearer. Absent on older servers / pre-org teams — treat as
   * optional and fall back to the legacy host-key path when missing.
   */
  agent_session_token?: string | null;
  expires_at: number;
  session_id: string;
  agent: { agent_id: string; code_name: string; team_id: string };
  artifacts: { 'CLAUDE.md': string; '.mcp.json': string };
  /**
   * ENG-6278: out-of-band integration secrets (e.g. `COMPOSIO_API_KEY`) the
   * server ships SEPARATELY from the on-disk artifacts. We inject these into
   * the impersonated Claude Code's LAUNCH ENV so the templated
   * `${COMPOSIO_API_KEY}` header in `.mcp.json` resolves at MCP-launch — the
   * key never touches disk. Absent on older servers / when
   * ENABLE_IMPERSONATION_INTEGRATIONS is off / when the agent has no connected
   * Composio toolkit.
   */
  integration_env?: Record<string, string> | null;
}

// ---------------------------------------------------------------------------
// `agt impersonate connect <token>`
// ---------------------------------------------------------------------------

/**
 * Options for the connect command.
 *
 * `noLaunch` defaults to `false` — connect spawns Claude Code in the
 * impersonation project directory after a successful swap. Operators
 * opt out with `--no-launch` (useful for scripting, smoke tests, or
 * when they want to manage their own Claude session).
 *
 * ENG-5747.
 */
export interface ImpersonateConnectOptions {
  noLaunch?: boolean;
  /**
   * ENG-5756 (AC3): swap into an auto-provisioned dedicated directory
   * (`~/.augmented-impersonate/<code-name>/workdir`) rather than the
   * operator's current directory. Makes a repo clobber structurally
   * impossible — the swap never touches cwd. Also the escape hatch the
   * git-work-tree guard points operators at.
   */
  workdir?: boolean;
  /**
   * Override the API host used to redeem the token. When unset, impersonate
   * always hits the production API (`api.augmented.team`) regardless of
   * `AGT_HOST` — operators routinely point AGT_HOST at a local/Tailscale
   * dev API for day-to-day CLI work, but redeem tokens are minted against
   * prod almost every time. Pin the default to prod and require an explicit
   * `--api-host` to deviate.
   */
  apiHost?: string;
}

export async function impersonateConnectCommand(
  token: string,
  options: ImpersonateConnectOptions = {},
): Promise<void> {
  requireImpersonateEnabled();

  const json = isJsonMode();
  // In JSON mode we never launch Claude Code — the caller is a script
  // that needs the structured exit, not an interactive shell. This is
  // independent of --no-launch (which is the explicit operator opt-out).
  const shouldLaunchClaude = !options.noLaunch && !json;

  // ENG-5688: validate token shape up-front (XXXX-XXXX) so we refuse
  // obvious garbage before a server round-trip. Server does the
  // authoritative validation against impersonation_setup_tokens.
  if (!REDEEM_TOKEN_RE.test(token)) {
    const msg =
      'Token is not in the expected XXXX-XXXX format. ' +
      'Copy the full `agt impersonate connect <token>` command from the webapp.';
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }

  // ── Guard: refuse a second session for the SAME target directory ──────
  //
  // ENG-5967: impersonation state is keyed per project directory, so
  // different directories (e.g. two separate terminals, each in its own
  // dedicated dir) run concurrently. We only refuse re-connecting a
  // directory that already has an active session. In the default mode the
  // target is process.cwd(), known up-front — check it here so a duplicate
  // refuses BEFORE the single-use redeem token is spent. In `--workdir`
  // mode the target depends on the agent code_name (resolved post-redeem),
  // so that case is guarded again just before the swap (see below).
  if (!options.workdir) {
    const existing = readActiveManifest(process.cwd());
    if (existing) {
      const msg =
        `An impersonation session is already active in this directory (${existing.code_name}). ` +
        `Run \`agt impersonate exit\` here first, or connect from a different directory.`;
      if (json) jsonOutput({ ok: false, error: msg });
      else error(msg);
      process.exitCode = 1;
      return;
    }
  }

  // ── Guard: never swap persona files inside a git working tree ─────────
  //
  // ENG-5756: the swap (below) symlinks the agent's CLAUDE.md/.mcp.json
  // into the swap target. In the default mode that target is
  // process.cwd(); if that's inside a git work tree, the swap overwrites
  // the repo's OWN CLAUDE.md/.mcp.json with symlinks — git sees a
  // type-change and a stray `git commit -a` could capture them,
  // corrupting the repo. Impersonation is designed for a standalone /
  // scratch directory, so refuse here — crucially BEFORE the redeem
  // round-trip, so the single-use token isn't spent and no files are
  // touched — and point the operator at `--workdir` or a clean dir.
  //
  // `--workdir` opts into an auto-provisioned dedicated directory under
  // ~/.augmented-impersonate/ (resolved after redeem, once we know the
  // code_name), so cwd is never touched and this guard doesn't apply.
  if (!options.workdir) {
    const workTreeRoot = findGitWorkTreeRoot(process.cwd());
    if (workTreeRoot) {
      const msg =
        `Refusing to impersonate inside a git working tree (${workTreeRoot}). ` +
        `connect swaps the agent's ${SWAP_FILES.join(' + ')} into the current ` +
        `directory, which would overwrite the repo's own files. Re-run with ` +
        `\`--workdir\` to use an auto-provisioned dedicated directory, or run ` +
        `from a standalone empty directory.`;
      if (json) jsonOutput({ ok: false, error: msg });
      else error(msg);
      process.exitCode = 1;
      return;
    }
  }

  if (!json) console.log(chalk.bold('\nAugmented — Impersonate\n'));
  // The token is opaque (no embedded agent name to display, unlike the
  // old JWT shape) so the spinner message is generic. The success
  // message will show the real agent name from the bundle.
  const spinner = ora({
    text: 'Redeeming token…',
    isSilent: json,
  });
  spinner.start();

  // ── Call /redeem WITHOUT auth ────────────────────────────────────────
  //
  // This is the heart of AC4: the redeem token IS the credential, and
  // the server endpoint is PUBLIC. We use plain fetch() (not api.post)
  // because api.post triggers AGT_API_KEY discovery + exchange — which
  // would fail in a clean shell, and which we deliberately don't want
  // to need.
  // Pin to prod by default — see ImpersonateConnectOptions.apiHost.
  const host = (options.apiHost ?? PROD_AGT_HOST).replace(/\/+$/, '');
  let bundle: RedeemBundle;
  try {
    const res = await fetch(`${host}/impersonate/agent/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ redeem_token: token }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      const detail = (body['error'] as string) ?? `HTTP ${res.status}`;
      throw new Error(`${detail} (HTTP ${res.status})`);
    }
    bundle = (await res.json()) as RedeemBundle;
  } catch (err) {
    spinner.fail('Failed to redeem token.');
    const msg = err instanceof Error ? err.message : String(err);
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }

  // ── Write persona files to ~/.augmented-impersonate/<code-name>/ ─────
  spinner.text = 'Writing persona files…';
  const personaDir = ensureCodeNameDir(bundle.agent.code_name);
  writeFileSync(
    join(personaDir, 'CLAUDE.md'),
    bundle.artifacts['CLAUDE.md'],
  );
  // ENG-5812: the server-rendered .mcp.json is shaped for the agent's
  // Lambda/Docker runtime (paths under `/home/sbx_user1051/.augmented/_mcp/`
  // and empty AGT_HOST / AGT_API_KEY / HOME). Rewrite it so the
  // operator's local Claude Code can actually spawn the MCP servers
  // — substitute Lambda paths for the CLI's own bundled `dist/mcp/`
  // and fill the env with operator-side values. See
  // ../lib/impersonate-mcp-rewrite.ts for the contract.
  writeFileSync(
    join(personaDir, '.mcp.json'),
    rewriteMcpJsonForImpersonation(bundle.artifacts['.mcp.json'], {
      operatorHome: process.env.HOME ?? '',
      operatorPath: resolveOperatorPath(),
      cliMcpBundleDir: resolveCliMcpBundleDir(),
      impersonationToken: bundle.token,
      agentSessionToken: bundle.agent_session_token ?? null,
      apiHost: host,
      agentId: bundle.agent.agent_id,
      agentCodeName: bundle.agent.code_name,
    }),
  );

  // ── Swap files + write manifest atomically ───────────────────────────
  //
  // CR #3321347793 from PR #1489: this whole step has to be atomic
  // from the operator's perspective. If we swap one file and then
  // the second swap or the manifest write fails, the project ends up
  // modified with no manifest to drive recovery from `exit`. Track
  // each successful swap and roll back on any failure.
  spinner.text = 'Swapping persona files…';
  // Default target is the operator's cwd (guarded above against git work
  // trees). `--workdir` instead provisions a dedicated agent-scoped dir
  // under ~/.augmented-impersonate/<code-name>/workdir so the swap can
  // never touch a repo (ENG-5756 AC3). Everything downstream — manifest
  // project_cwd, the spawn cwd, and `exit`'s restore target — keys off
  // this one value, so the rest of the flow is mode-agnostic.
  const projectCwd = options.workdir
    ? ensureImpersonateWorkdir(bundle.agent.code_name)
    : process.cwd();

  // ENG-5967: per-directory guard, second half. The default-mode guard up
  // top already ran against process.cwd(); this catches the `--workdir`
  // case, whose target dir is only known now (it's derived from the
  // code_name in the redeem bundle). Re-connecting the same agent's workdir
  // while a session is live would otherwise clobber its backup state.
  const existingForTarget = readActiveManifest(projectCwd);
  if (existingForTarget) {
    spinner.fail('Impersonation already active for this agent.');
    const msg =
      `An impersonation session is already active in ${projectCwd} (${existingForTarget.code_name}). ` +
      `Run \`agt impersonate exit\` there first.`;
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }
  const backups: Record<string, BackupKind> = {};
  const swapped: Array<{ file: string; kind: BackupKind }> = [];
  // ENG-5750: track the introduce hook so the catch below can roll it
  // back alongside the file swaps if the manifest write fails.
  let hookBackup: HookBackup | undefined;
  let statusLineBackup: StatusLineBackup | undefined;

  try {
    for (const file of SWAP_FILES) {
      const projectPath = join(projectCwd, file);
      const kind = swapInPersonaFile(projectPath, join(personaDir, file));
      backups[file] = kind;
      swapped.push({ file, kind });
    }

    // ENG-5750: register the SessionStart "introduce yourself" hook in
    // the project dir so the fresh Claude Code session opens with the
    // agent introducing itself + its integrations. Folded into the
    // manifest so `exit` removes it and leaves the cwd clean.
    hookBackup = registerIntroduceHook(projectCwd);

    // ENG-5801: register the project-scoped statusLine so the operator's
    // Claude Code session shows a 🤖 <agent-name> badge while they're in
    // this project. Backup folded into the manifest so `exit` reverses it
    // byte-for-byte. Shares the same settings.local.json backup file as
    // the introduce hook — whichever register ran first captured the
    // operator's true original.
    statusLineBackup = registerStatusLine(projectCwd);

    const manifest: ImpersonateManifest = {
      code_name: bundle.agent.code_name,
      agent_id: bundle.agent.agent_id,
      team_id: bundle.agent.team_id,
      token: bundle.token,
      expires_at: bundle.expires_at,
      session_id: bundle.session_id,
      minted_at: new Date().toISOString(),
      project_cwd: projectCwd,
      host,
      backups,
      hook: hookBackup,
      statusLine: statusLineBackup,
    };
    writeActiveManifest(manifest);
  } catch (err) {
    if (statusLineBackup) {
      try {
        unregisterStatusLine(statusLineBackup);
      } catch {
        // Best-effort; the original error is what matters.
      }
    }
    if (hookBackup) {
      try {
        unregisterIntroduceHook(hookBackup);
      } catch {
        // Best-effort; the original error is what matters.
      }
    }
    for (const { file, kind } of [...swapped].reverse()) {
      try {
        restoreSwapped(join(projectCwd, file), kind);
      } catch {
        // Swallow rollback-of-rollback failures — the original error
        // is what the operator needs to see.
      }
    }
    spinner.fail('Failed to enter impersonation; rolled back changes.');
    throw err;
  }

  spinner.stop();
  if (json) {
    jsonOutput({
      ok: true,
      code_name: bundle.agent.code_name,
      agent_id: bundle.agent.agent_id,
      session_id: bundle.session_id,
      expires_at: bundle.expires_at,
      project_cwd: projectCwd,
      host,
      swapped_files: SWAP_FILES,
    });
    return;
  }

  success(`Impersonating ${chalk.bold(bundle.agent.code_name)}`);
  console.log();
  console.log(`  Agent ID:   ${chalk.dim(bundle.agent.agent_id)}`);
  console.log(`  Team:       ${chalk.dim(bundle.agent.team_id)}`);
  console.log(`  Session:    ${chalk.dim(bundle.session_id)}`);
  console.log(
    `  Expires:    ${chalk.dim(new Date(bundle.expires_at * 1000).toISOString())}`,
  );
  console.log(`  Project:    ${chalk.dim(projectCwd)}`);
  console.log(`  Host:       ${chalk.dim(host)}`);
  console.log(`  Swapped:    ${chalk.dim(SWAP_FILES.join(', '))}`);
  console.log();
  if (!shouldLaunchClaude) {
    // --no-launch (or JSON mode, but the JSON branch already returned
    // above). Operator drove their own session start; keep the original
    // copy so they know they have to restart Claude themselves.
    if (options.workdir) {
      // The persona was swapped into the dedicated workdir, not the
      // operator's cwd — tell them where to start their session.
      info(`Start Claude Code in the impersonation workdir: \`cd ${projectCwd}\``);
    }
    info(
      "Restart Claude Code to pick up the agent's persona + MCP server set. " +
        'Claude Code 2.1.152 does not honour `tools/list_changed` in-session ' +
        '(spike PoC, ENG-4733).',
    );
    console.log();
    info('When done: `agt impersonate exit`');
    return;
  }

  // ── Hand off to Claude Code ──────────────────────────────────────────
  //
  // Spawn `claude` in the impersonation project dir with the parent's
  // stdio so the operator immediately enters an interactive session
  // (foreground replace from their perspective; we wait on the child
  // and mirror its exit code so shells and scripts behave naturally).
  //
  // We deliberately call `claude` via PATH lookup — no config knob in
  // v1, no AGT_CLAUDE_BIN override. Revisit if multiple installs become
  // common; until then keep the contract simple.
  //
  // ENOENT (claude not on PATH) falls back to the manual-restart copy
  // + non-zero exit so the operator knows the auto-launch didn't fire
  // and what to do instead. Anything else surfaces verbatim.
  info('Launching Claude Code… (use `--no-launch` next time to skip)');
  console.log();

  const launch = buildImpersonateClaudeLaunch(
    projectCwd,
    process.env,
    bundle.agent.agent_id,
    bundle.integration_env ?? null,
  );

  await new Promise<void>((resolve) => {
    const child = spawn('claude', launch.args, {
      stdio: 'inherit',
      cwd: projectCwd,
      env: launch.env,
    });
    child.on('error', (err: NodeJS.ErrnoException) => {
      if (err.code === 'ENOENT') {
        error(
          '`claude` binary not found on PATH. Start Claude Code yourself ' +
            `from ${projectCwd}, or install the CLI from claude.ai/code.`,
        );
        process.exitCode = 1;
      } else {
        error(`Failed to launch Claude Code: ${err.message}`);
        process.exitCode = 1;
      }
      resolve();
    });
    child.on('exit', (code, signal) => {
      // Mirror the child's exit so the operator's shell sees the right
      // status. A signal exit (e.g. operator hit Ctrl-C in Claude) is
      // reported the conventional way (128 + signum is overkill here;
      // Node's process.exitCode = null on signal is fine — the parent
      // already inherited the SIGINT and is being torn down anyway).
      if (code !== null && code !== 0) process.exitCode = code;
      if (signal) process.exitCode = 130; // standard SIGINT semantics
      resolve();
    });
  });

  // The impersonation manifest still lives — `agt impersonate exit`
  // will tear it down. Remind the operator on the way out so they don't
  // forget; without it, the persona files stay swapped indefinitely.
  console.log();
  info('When done: `agt impersonate exit` (still active).');
}

// ---------------------------------------------------------------------------
// `agt impersonate current`
// ---------------------------------------------------------------------------

/** Render one manifest as the human-readable block `current` prints. */
function printManifestText(manifest: ImpersonateManifest): void {
  const expired = isExpired(manifest);
  console.log();
  console.log(chalk.bold(`Impersonating: ${manifest.code_name}`));
  console.log(`  Agent ID:    ${chalk.dim(manifest.agent_id)}`);
  console.log(`  Team:        ${chalk.dim(manifest.team_id)}`);
  console.log(`  Session:     ${chalk.dim(manifest.session_id)}`);
  console.log(`  Minted at:   ${chalk.dim(manifest.minted_at)}`);
  console.log(
    `  Expires at:  ${chalk.dim(new Date(manifest.expires_at * 1000).toISOString())} ` +
      (expired ? chalk.red('(EXPIRED — run `agt impersonate exit`)') : ''),
  );
  console.log(`  Project:     ${chalk.dim(manifest.project_cwd)}`);
  console.log(`  Host:        ${chalk.dim(manifest.host)}`);
  console.log();
}

/** Shape one manifest into the JSON `current` emits. */
function manifestToJson(manifest: ImpersonateManifest) {
  return {
    code_name: manifest.code_name,
    agent_id: manifest.agent_id,
    team_id: manifest.team_id,
    session_id: manifest.session_id,
    minted_at: manifest.minted_at,
    expires_at: manifest.expires_at,
    expired: isExpired(manifest),
    project_cwd: manifest.project_cwd,
    host: manifest.host,
  };
}

export async function impersonateCurrentCommand(
  opts: { all?: boolean } = {},
): Promise<void> {
  requireImpersonateEnabled();

  const json = isJsonMode();

  // ENG-5967: `--all` reports every active session on the machine; the
  // default reports the session for the current directory (with a hint
  // when others are running elsewhere).
  if (opts.all) {
    const manifests = listActiveManifests();
    if (json) {
      jsonOutput({ ok: true, active: manifests.map(manifestToJson) });
      return;
    }
    if (manifests.length === 0) {
      info('No active impersonations.');
      return;
    }
    console.log();
    console.log(chalk.bold(`Active impersonations (${manifests.length}):`));
    for (const m of manifests) printManifestText(m);
    return;
  }

  const manifest = readActiveManifest(process.cwd());
  if (!manifest) {
    const others = listActiveManifests();
    if (json) {
      jsonOutput({ ok: true, active: null, others_active: others.length });
      return;
    }
    if (others.length > 0) {
      info(
        `No active impersonation in this directory — ${others.length} active elsewhere. ` +
          'Run `agt impersonate current --all` to list them.',
      );
    } else {
      info('No active impersonation.');
    }
    return;
  }

  if (json) {
    jsonOutput({ ok: true, active: manifestToJson(manifest) });
    return;
  }
  printManifestText(manifest);
}

// ---------------------------------------------------------------------------
// `agt impersonate exit`
// ---------------------------------------------------------------------------

export async function impersonateExitCommand(
  opts: { all?: boolean } = {},
): Promise<void> {
  requireImpersonateEnabled();

  const json = isJsonMode();

  // ENG-5967: `--all` tears down every active session on the machine; the
  // default exits only the session for the current directory, leaving any
  // concurrent sessions in other directories untouched.
  if (opts.all) {
    const manifests = listActiveManifests();
    if (manifests.length === 0) {
      if (json) jsonOutput({ ok: true, was_active: false, exited: [] });
      else info('No active impersonation to exit.');
      return;
    }
    const exited: string[] = [];
    for (const manifest of manifests) {
      // Silent per-session so a single combined result is emitted below
      // instead of N interleaved blocks (and JSON stays one object).
      await performImpersonateExit(manifest, { json, silent: true });
      exited.push(manifest.code_name);
    }
    if (json) {
      jsonOutput({ ok: true, was_active: true, exited });
    } else {
      success(`Exited ${exited.length} impersonation session(s): ${exited.join(', ')}`);
      info('Restart Claude Code to pick up your original persona.');
    }
    return;
  }

  const manifest = readActiveManifest(process.cwd());
  if (!manifest) {
    if (json) jsonOutput({ ok: true, was_active: false });
    else info('No active impersonation to exit.');
    return;
  }

  await performImpersonateExit(manifest, { json, silent: false });
}

/**
 * ENG-5911 — the cleanup half of `exit`, factored out so it runs both from
 * the explicit `agt impersonate exit` command and from the lazy auto-exit
 * (autoExitExpiredImpersonation). Deliberately NOT gated on
 * requireImpersonateEnabled(): cleaning up a lingering session must always be
 * allowed, even from a shell where the impersonate flag isn't set. When
 * `silent` (the auto-exit path) it performs the same side effects but emits no
 * stdout result — so it can't corrupt a JSON command's output or spam an
 * unrelated command; the caller prints its own one-line stderr notice.
 */
async function performImpersonateExit(
  manifest: ImpersonateManifest,
  opts: { json: boolean; silent: boolean },
): Promise<void> {
  const { json, silent } = opts;

  // ── Notify the server (best-effort) ───────────────────────────────────
  //
  // /stop currently requires Bearer auth too (ENG-5687); a follow-up will
  // let it accept JUST X-Agent-Impersonation. For v0, if AGT_API_KEY is
  // present we use api.post (which auto-attaches Bearer); if not, we go
  // direct via fetch — the server will likely reject, but local cleanup
  // proceeds either way and the token expires naturally.
  let stopOk = true;
  let stopDetail: string | null = null;
  try {
    if (process.env['AGT_API_KEY']) {
      await api.post('/impersonate/agent/stop', undefined, {
        [AGENT_IMPERSONATION_HEADER]: manifest.token,
      });
    } else {
      // Clean-shell fallback. Sends only X-Agent-Impersonation; the
      // server side will reject this in v0 (auth middleware demands
      // Bearer), but the operator's local cleanup is what matters here.
      const res = await fetch(`${manifest.host}/impersonate/agent/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [AGENT_IMPERSONATION_HEADER]: manifest.token,
        },
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
        const detail = (body['error'] as string) ?? `HTTP ${res.status}`;
        stopOk = false;
        stopDetail = `${detail} (HTTP ${res.status})`;
      }
    }
  } catch (err) {
    stopOk = false;
    stopDetail = err instanceof ApiError
      ? `${err.message} (HTTP ${err.status})`
      : err instanceof Error
        ? err.message
        : String(err);
    // Don't return — local cleanup still has to run.
  }

  // ── Restore the operator's project files ──────────────────────────────
  const restored: string[] = [];
  for (const [file, kind] of Object.entries(manifest.backups)) {
    const projectPath = join(manifest.project_cwd, file);
    try {
      restoreSwapped(projectPath, kind);
      restored.push(file);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!json) error(`Failed to restore ${file}: ${msg}`);
    }
  }

  // ── Remove the project-scoped statusLine (ENG-5801) ───────────────────
  // Restores the operator's prior statusLine value byte-for-byte from the
  // backup, or removes the settings.local.json we created. Runs BEFORE
  // unregisterIntroduceHook because both touch the same backup file and
  // whichever runs first does the restore — running statusLine first
  // means a malformed-or-stripped fallback path is hit there, not here.
  // Older manifests have no `statusLine` field.
  if (manifest.statusLine) {
    try {
      unregisterStatusLine(manifest.statusLine);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!json) error(`Failed to remove statusLine: ${msg}`);
    }

    // The statusline script renames the terminal tab to `🤖 <agent>` on
    // every refresh while impersonation is active. Now that it's gone,
    // emit a one-shot OSC 1 reset so the tab title falls back to the
    // terminal's default instead of staying stuck on the agent name
    // until the next process changes it. JSON-mode skips this — JSON
    // callers are scripts and a control sequence in the JSON stream
    // would corrupt the parse.
    if (!json && process.stdout.isTTY) {
      process.stdout.write('\x1b]1;\x07');
    }
  }

  // ── Remove the introduce SessionStart hook (ENG-5750) ─────────────────
  // Restores/removes .claude/settings.local.json so the operator's cwd
  // is left exactly as connect found it. Older manifests have no `hook`.
  if (manifest.hook) {
    try {
      unregisterIntroduceHook(manifest.hook);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (!json) error(`Failed to remove introduce hook: ${msg}`);
    }
  }

  clearActiveManifest(manifest.project_cwd);

  // Auto-exit path: side effects done, emit nothing (the caller prints a
  // one-line stderr notice; staying silent keeps unrelated/JSON command
  // output clean).
  if (silent) return;

  if (json) {
    jsonOutput({
      ok: true,
      was_active: true,
      session_id: manifest.session_id,
      stop_acknowledged: stopOk,
      stop_detail: stopDetail,
      restored,
    });
    return;
  }

  success(`Exited impersonation: ${manifest.code_name}`);
  if (restored.length > 0) {
    info(`Restored: ${restored.join(', ')}`);
  }
  if (!stopOk) {
    info(`Note: server-side stop not acknowledged (${stopDetail}). Token will expire naturally.`);
  }
  info('Restart Claude Code to pick up your original persona.');
}

/**
 * ENG-5911 — lazy auto-exit. Called from the CLI pre-action hook before
 * (almost) every command: if an impersonation session is active AND its token
 * has expired, run the same cleanup `agt impersonate exit` would — restore the
 * operator's files, post the stop audit (best-effort), clear the manifest — so
 * a dead session never has to be exited by hand. Cheap no-op when nothing is
 * active or the session is still valid; never throws into the caller's command.
 *
 * Skipped by the hook for `impersonate introduce` (the SessionStart hook —
 * must never restore files mid-boot) and `impersonate exit` (redundant).
 * Revocation (ENG-5875) will widen the "should auto-exit" predicate beyond
 * pure expiry, reusing performImpersonateExit unchanged.
 */
export async function autoExitExpiredImpersonation(): Promise<void> {
  // ENG-5967: state is keyed per directory and sessions can run
  // concurrently, so sweep ALL of them and clean up every expired one —
  // not just whichever the old singleton happened to hold. Cheap when
  // nothing is active (empty list) and never throws into the caller.
  let manifests: ImpersonateManifest[];
  try {
    manifests = listActiveManifests();
  } catch {
    return;
  }
  const expired = manifests.filter((m) => isExpired(m));
  if (expired.length === 0) return;
  for (const manifest of expired) {
    try {
      if (!isJsonMode()) {
        // Notice goes to STDERR (not info()'s stdout) so it can't pollute the
        // stdout of the unrelated command this auto-exit is piggybacking on
        // (e.g. `agt whoami | grep`). Same info styling, different stream.
        console.error(
          chalk.cyan(
            `ℹ Impersonation session for ${manifest.code_name} expired — auto-exiting and restoring your files.`,
          ),
        );
      }
      await performImpersonateExit(manifest, { json: isJsonMode(), silent: true });
    } catch {
      // Auto-exit is best-effort; never break the operator's actual command.
    }
  }
}

// ---------------------------------------------------------------------------
// `agt impersonate introduce` (hidden) — SessionStart hook target
// ---------------------------------------------------------------------------

/**
 * Print the impersonated agent's first-person introduction to stdout so
 * Claude Code injects it into the fresh session (ENG-5750). Registered
 * by `connect` as a SessionStart (matcher `startup`) hook.
 *
 *  - **Guarded**: emits only when an active, unexpired manifest exists.
 *    After `exit` (or once the token lapses) it's a silent no-op — that's
 *    how a normal operator session in the same cwd produces no intro.
 *  - **Snapshot-only**: reads CLAUDE.md / .mcp.json from project_cwd;
 *    never the network, never a token.
 *  - **Soft-fail**: any error prints whatever is available and exits 0,
 *    never blocking session boot. Deliberately NOT gated on
 *    requireImpersonateEnabled() — connect only registers the hook when
 *    the flag was set, and a hook must never throw into the session.
 */
export async function impersonateIntroduceCommand(): Promise<void> {
  try {
    // ENG-5967: the SessionStart hook runs in the impersonation session's
    // own directory (connect spawns Claude with cwd = project_cwd), so the
    // current directory's manifest is the right — and only — one to read.
    const manifest = readActiveManifest(process.cwd());
    if (!manifest || isExpired(manifest)) return;

    const cwd = manifest.project_cwd;
    const identity = readClaudeMdIdentity(join(cwd, 'CLAUDE.md'));
    const integrations = readMcpIntegrations(join(cwd, '.mcp.json'));

    const intro = buildIntroduction({
      displayName: identity.displayName,
      codeName: manifest.code_name,
      role: identity.role,
      integrations,
    });
    process.stdout.write(intro + '\n');
  } catch {
    // Soft-fail: never block session boot.
  }
}

function readClaudeMdIdentity(path: string): ParsedIdentity {
  try {
    return parseIdentityFromClaudeMd(readFileSync(path, 'utf-8'));
  } catch {
    return { displayName: null, role: null };
  }
}

function readMcpIntegrations(path: string): string[] {
  try {
    return parseIntegrationsFromMcpJson(readFileSync(path, 'utf-8'));
  } catch {
    return [];
  }
}

/**
 * ENG-5812 — resolve the CLI's bundled `dist/mcp/` directory, the same
 * one populated by `build:mcp-assets` (copies of @integrity-labs/
 * augmented-mcp's index.js + channel bundles). Used by the .mcp.json
 * rewrite to substitute the server's Lambda paths for operator-side
 * paths.
 *
 * Walks from this module's location to handle the same layouts as
 * resolveBundledAssetPath in impersonate-statusline.ts:
 *  - dev (tsx):     apps/cli/src/commands → apps/cli/mcp (workspace copy)
 *  - built (tsup):  apps/cli/dist/<chunk>.js → apps/cli/dist/mcp
 *  - installed:     <pkg>/dist/<chunk>.js → <pkg>/dist/mcp
 *
 * Returns a best-effort path when no candidate currently exists on
 * disk — CI runs from source without `build:mcp-assets` having
 * executed, and the impersonate-connect tests exercise this code path
 * without actually spawning the MCP servers. The runtime spawn path
 * surfaces "MCP server failed to start" loudly enough already; making
 * resolveCliMcpBundleDir throw here would gate the rewrite on a
 * pre-built tree the test environment doesn't have.
 */
function resolveCliMcpBundleDir(): string {
  const moduleDir = dirname(fileURLToPath(import.meta.url));
  const candidates = [
    join(moduleDir, 'mcp'),
    join(moduleDir, '..', 'mcp'),
    join(moduleDir, '..', '..', 'mcp'),
  ];
  for (const candidate of candidates) {
    if (existsSync(join(candidate, 'index.js'))) return candidate;
  }
  // Best-effort fallback — the most likely "correct" path for a built
  // install. An operator whose installed CLI is missing dist/mcp/ would
  // see a spawn ENOENT when the augmented gateway tries to start,
  // which is the right loud failure mode (not a silent rewrite throw).
  return candidates[candidates.length - 1]!;
}

/**
 * ENG-5818 (Bug 1) — build a host-correct `PATH` for the operator's
 * machine to overwrite the server-baked Lambda PATH in the rendered
 * `.mcp.json`. The server bakes its own runtime PATH at provision time
 * (`/var/lang/bin:/usr/local/bin:/usr/bin/:/bin:/opt/bin` on the redeem
 * Lambda); none of those dirs hold `node` on a macOS nvm/Homebrew host,
 * so the MCP `command: "node"` spawn ENOENTs and every server fails.
 *
 * Prepend the running node's own bin dir (`dirname(process.execPath)` —
 * the exact interpreter running `agt`, guaranteed to exist) to the
 * operator's inherited `PATH`, de-duplicated so we don't bloat the
 * variable on repeated connects. This guarantees `node` resolves while
 * preserving the operator's other PATH entries (Homebrew etc.) so `npx`
 * still works for integration MCP servers.
 *
 * Exported for tests.
 */
export function resolveOperatorPath(
  execPath: string = process.execPath,
  inheritedPath: string = process.env.PATH ?? '',
): string {
  const nodeBinDir = dirname(execPath);
  const segments = inheritedPath.split(delimiter).filter((s) => s.length > 0);
  if (!segments.includes(nodeBinDir)) {
    segments.unshift(nodeBinDir);
  }
  return segments.join(delimiter);
}

/**
 * ENG-5806 — return shape for `buildImpersonateClaudeLaunch`. Named so
 * the contract is reusable and discoverable, per project TS guidelines.
 */
export interface ImpersonateClaudeLaunch {
  args: string[];
  env: NodeJS.ProcessEnv;
}

/**
 * ENG-5806 — build the launch args + env so the impersonated Claude Code
 * session sees ONLY the agent's swapped-in `.mcp.json` and not the
 * operator's user-scope MCP servers (~/.claude.json) or claude.ai
 * connectors (Gmail/Calendar/Drive/Slack/etc.).
 *
 * Without this, Claude Code merges the project-level `.mcp.json` the
 * impersonation flow swapped in WITH the operator's user-scope tools,
 * so the impersonated agent silently wields the operator's credentials.
 * That breaks the contract of `impersonate` and is SOC2-relevant
 * (auditing "you acted as the agent" is misleading when the agent had
 * the operator's tools).
 *
 *  - `--strict-mcp-config` restricts the session to ONLY the `.mcp.json`
 *    files passed via `--mcp-config`, suppressing user-scope, project-
 *    scope auto-load, claude.ai connectors, and plugin-provided servers.
 *  - `ENABLE_CLAUDEAI_MCP_SERVERS=false` is belt-and-braces: claude.ai
 *    connectors only auto-load under claude.ai subscription auth (not
 *    API key), but setting it explicitly is cheap and survives an auth
 *    mode change mid-session.
 *  - ENG-6278: `integrationEnv` (e.g. `{ COMPOSIO_API_KEY: 'ak_…' }`) is
 *    folded into the launch env so Claude Code substitutes the templated
 *    `${COMPOSIO_API_KEY}` header in the agent's `composio_*` MCP entries at
 *    launch — the key stays in process env, never on disk. Only present when
 *    the server shipped `integration_env` (ENABLE_IMPERSONATION_INTEGRATIONS).
 *
 * Exported for tests; not part of the public CLI API.
 */
export function buildImpersonateClaudeLaunch(
  projectCwd: string,
  inheritEnv: NodeJS.ProcessEnv = process.env,
  agentId: string | null = null,
  integrationEnv: Record<string, string> | null = null,
): ImpersonateClaudeLaunch {
  const env: NodeJS.ProcessEnv = {
    ...inheritEnv,
    ENABLE_CLAUDEAI_MCP_SERVERS: 'false',
  };
  // ENG-5689: when we know which agent the operator is impersonating,
  // forward the agent UUID so spawned channel MCP servers (slack /
  // telegram / direct-chat) can refuse send-* tools. Spoof-resistance
  // is out of scope; this is the accidental-foot-gun gate.
  if (agentId !== null && agentId.length > 0) {
    env.AGT_ACT_AS_AGENT_ID = agentId;
  }
  // ENG-6278: late-bind the agent's integration secrets (Composio key) so the
  // `${COMPOSIO_API_KEY}` header templates resolve. Spread last so an explicit
  // server-shipped value wins over any stale inherited one.
  if (integrationEnv) {
    for (const [key, value] of Object.entries(integrationEnv)) {
      if (key && value) env[key] = value;
    }
  }
  return {
    args: [
      '--strict-mcp-config',
      '--mcp-config',
      join(projectCwd, '.mcp.json'),
    ],
    env,
  };
}
